import { z } from 'zod';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { splitFullName } from '@/lib/utils/user.utils';
import { phonePayload, phoneSchema, toPhoneFormFields } from '@/lib/utils/phone.utils';
import type { IndividualProfile, UpdateIndividualProfilePayload } from '@/lib/types/profile.types';

const S = UI_STRINGS.PROFILE;

/**
 * Individual account edit-mode form (FR-09/MYN-27, Figma node 176:471). One
 * "Full Name" field is split into `firstName`/`lastName` at submit time -
 * `UpdateIndividualAccountDto` on the backend still stores them separately.
 */
export const accountEditSchema = z.object({
  fullName: z.string().trim().min(1, S.FULL_NAME_REQUIRED),
  /**
   * E.164 (`+14155550142`) or empty; a number not valid for its country is refused (MYN-277).
   * Optional today, so empty is fine.
   */
  phone: phoneSchema(false),
  /**
   * A phone saved earlier as free text that is not a readable number. Form-only: shown beside the
   * field, never sent, and while it is untouched `phone` is omitted from the save so the legacy
   * value is neither rejected nor wiped.
   */
  legacyPhone: z.string().optional(),
  /**
   * The person's country as the countries-table id, NOT its name (A-50,
   * MYN-175). Optional: the field sheet marks country editable but not
   * required, so `undefined` is a valid saved state and clears the column.
   */
  countryId: z.number().int().positive().optional(),
  /**
   * Set only by the avatar upload control (MYN-175). `undefined` means "the
   * avatar was not touched" and is omitted from the payload; a string - `''`
   * included - is an explicit change the backend acts on. It is never seeded
   * from `profile.avatarUrl`: that field is a resolved presigned URL, which
   * the backend's key-format check would reject.
   */
  avatarFileKey: z.string().optional(),
});

export type AccountEditFormValues = z.infer<typeof accountEditSchema>;

export function toFormValues(profile: IndividualProfile): AccountEditFormValues {
  return {
    fullName: `${profile.firstName} ${profile.lastName}`.trim(),
    ...toPhoneFormFields(profile.phone),
    countryId: profile.countryId ?? undefined,
  };
}

/**
 * @param savedPhone - the phone stored today (`null` when none). With it, a phone the user emptied or a
 *   legacy value they removed is sent as `null` so it actually clears; without it an empty phone
 *   sends `''`, which the backend reads as "not sent".
 */
export function toApiPayload(
  values: AccountEditFormValues,
  savedPhone?: string | null,
): UpdateIndividualProfilePayload {
  const { firstName, lastName } = splitFullName(values.fullName);
  return {
    firstName,
    lastName,
    ...phonePayload(values.phone, values.legacyPhone, savedPhone),
    // `null`, not omitted: an unselected country is the explicit "clear it"
    // signal the backend acts on (`@IsOptional()` accepts `null`).
    countryId: values.countryId ?? null,
    // Omitted entirely unless the upload control set it - sending `''` on
    // every save would clear the stored avatar of anyone who edited their
    // name without re-uploading a photo.
    ...(values.avatarFileKey !== undefined && { avatarFileKey: values.avatarFileKey }),
  };
}
